import type {
  LeaderboardActivityType,
  LeaderboardBoardStatus,
  LeaderboardPrizeItem,
  LeaderboardSummaryStats,
} from '../../../domain/leaderboard/models/public-leaderboard.model';
import type { LeaderboardVisualTokens } from '../../../domain/leaderboard/models/leaderboard-visual-tokens.model';
import type { PublicOverviewApiData } from '../dtos/leaderboard-public-api.dto';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

function normalizeHex6(value: string | undefined | null): string | undefined {
  const trimmed = String(value ?? '').trim();
  return HEX6.test(trimmed) ? trimmed : undefined;
}

function mapBoardStatus(apiStatus: string): LeaderboardBoardStatus {
  const s = apiStatus.trim().toLowerCase();
  switch (s) {
    case 'borrador':
      return 'draft';
    case 'programado':
      return 'scheduled';
    case 'activa':
      return 'active';
    case 'pausada':
      return 'paused';
    case 'finalizada':
      return 'finished';
    case 'cancelada':
      return 'cancelled';
    default:
      return 'active';
  }
}

function formatParticipantsReached(count: number): string {
  const n = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
  const formatted = n.toLocaleString('es-CO');
  return `${formatted} participante${n === 1 ? '' : 's'} lo alcanzaron`;
}

function milestoneTitle(m: PublicOverviewApiData['milestones'][number]): string {
  if (m.is_final) {
    const top = m.description?.trim();
    return top ? `Premio Principal — ${top}` : 'Premio Principal';
  }
  const xp = m.xp_required;
  return `Bonificación de puntos — ${xp.toLocaleString('es-CO')} XP`;
}

export function mapMilestonesFromOverviewApi(
  milestones: PublicOverviewApiData['milestones'] | undefined,
): LeaderboardPrizeItem[] {
  if (!Array.isArray(milestones)) {
    return [];
  }
  return milestones.map((m) => ({
    id: String(m.id),
    isFeatured: Boolean(m.is_final),
    title: milestoneTitle(m),
    description: String(m.description ?? '').trim(),
    xpRequired: m.is_final ? undefined : m.xp_required,
    participantsReachedLabel: m.is_final
      ? undefined
      : formatParticipantsReached(m.participants_reached_count),
  }));
}

function normalizeActivityType(raw: string | undefined): LeaderboardActivityType {
  return String(raw ?? '').trim().toLowerCase() === 'threshold' ? 'threshold' : 'competitive';
}

export function mapOverviewSummaryFromApi(data: PublicOverviewApiData): LeaderboardSummaryStats {
  const activityType = normalizeActivityType(data.activity_type);
  const top = data.top_winner_count ?? 0;
  const xpWinThreshold = Math.max(0, Math.trunc(data.xp_win_threshold ?? 0));
  const winnersReachedCount = Math.max(0, Math.trunc(data.winners_reached_count ?? 0));

  if (activityType === 'threshold' && xpWinThreshold > 0) {
    const xpLabel = xpWinThreshold.toLocaleString('es-CO');
    const winnersSubLabel =
      winnersReachedCount > 0
        ? `${winnersReachedCount.toLocaleString('es-CO')} alcanzaron la meta`
        : undefined;
    return {
      participantsCount: data.participants_count ?? 0,
      totalXp: data.total_xp ?? 0,
      activityType,
      winnersStatLabel: 'META XP',
      winnersLabel: xpLabel,
      xpWinThreshold,
      winnersReachedCount,
      winnersSubLabel,
    };
  }

  return {
    participantsCount: data.participants_count ?? 0,
    totalXp: data.total_xp ?? 0,
    activityType: 'competitive',
    winnersStatLabel: 'GANADORES',
    winnersLabel: top > 0 ? `Top ${top}` : 'Top',
  };
}

export function mapOverviewBrandingToVisualTokens(
  branding: PublicOverviewApiData['branding'] | undefined,
): Partial<LeaderboardVisualTokens> | undefined {
  if (!branding) {
    return undefined;
  }

  const primary = normalizeHex6(branding.primary_color);
  const secondary = normalizeHex6(branding.secondary_color);
  const tertiary = normalizeHex6(branding.tertiary_color);
  const backgroundImage = String(branding.background_url ?? '').trim() || undefined;

  if (!primary && !secondary && !tertiary && !backgroundImage) {
    return undefined;
  }

  return {
    ...(primary ? { primary } : {}),
    ...(secondary ? { secondary } : {}),
    ...(tertiary ? { tertiary } : {}),
    ...(backgroundImage ? { backgroundImage } : {}),
  };
}

export function mapOverviewBrandingLogoUrl(
  branding: PublicOverviewApiData['branding'] | undefined,
): string | undefined {
  const url = String(branding?.logo_url ?? '').trim();
  return url || undefined;
}

export interface MappedOverview {
  boardId: string;
  title: string;
  status: LeaderboardBoardStatus;
  activityType: LeaderboardActivityType;
  summary: LeaderboardSummaryStats;
  prizes: LeaderboardPrizeItem[];
  lastUpdatedIso: string;
  branding?: Partial<LeaderboardVisualTokens>;
  logoUrl?: string;
  termsAndConditionsUrl?: string;
}

export function mapOverviewApiToDomain(data: PublicOverviewApiData | undefined): MappedOverview | null {
  if (!data || !String(data.name ?? '').trim()) {
    return null;
  }
  const branding = mapOverviewBrandingToVisualTokens(data.branding);
  const logoUrl = mapOverviewBrandingLogoUrl(data.branding);
  const summary = mapOverviewSummaryFromApi(data);
  const termsUrl = String(data.terms_and_conditions_url ?? '').trim();
  return {
    boardId: String(data.leaderboard_id ?? ''),
    title: String(data.name).trim(),
    status: mapBoardStatus(String(data.status ?? '')),
    activityType: summary.activityType,
    summary,
    prizes: mapMilestonesFromOverviewApi(data.milestones),
    lastUpdatedIso: String(data.last_updated_at ?? '').trim() || new Date().toISOString(),
    branding,
    logoUrl,
    termsAndConditionsUrl: termsUrl || undefined,
  };
}
